import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, MessageSquare, BarChart3, FileText, Code, Brain, PieChart, LayoutGrid } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

import FileUpload from '../components/FileUpload';
import QueryInterface from '../components/QueryInterface';
import SQLQueryInterface from '../components/SQLQueryInterface';
import DataViewer from '../components/DataViewer';
import FileManager from '../components/FileManager';
import BusinessAnalysis from '../components/BusinessAnalysis';
import DataProfile from '../components/DataProfile';
import Onboarding from '../components/Onboarding';
import TourOverlay from '../components/TourOverlay';
import Templates from '../components/Templates';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import UsageWarning from '../components/UsageWarning';
import duckdbService from '../services/duckdbService';
import bundleService from '../services/bundleService';
import usageService from '../services/usageService';
import { useAuth } from '../context/AuthContext';
import AuthModal from '../components/AuthModal';

import DemoDatasetModal from '../components/DemoDatasetModal';
import { DEMO_DATASETS } from '../data/demoDatasets';
import billingService from '../services/billingService';

function DashboardPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, firebaseUser, authLoading, sessionLoading, sessionError, usageStatus, billingStatus, refreshUsage, fetchSessionUser, fetchBillingStatus, confirmCheckoutSession } = useAuth();
  const [activeTab, setActiveTab] = useState('upload');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sampleLoading, setSampleLoading] = useState(false);
  const [prefillQuestion, setPrefillQuestion] = useState({ text: '', token: 0 });
  const [showTour, setShowTour] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);

  const [showDemoModal, setShowDemoModal] = useState(false);
  const [billingActionLoading, setBillingActionLoading] = useState(false);
  const [demoMode, setDemoMode] = useState(false);
  const [demoDatasetId, setDemoDatasetId] = useState('ecommerce');
  const [showFreeBanner, setShowFreeBanner] = useState(false);
  const tourStorageKey = 'duckpilot_tour_seen';
  const totalStorageBytes = uploadedFiles.reduce(
    (sum, file) => sum + (Number(file.file_size_bytes) || 0),
    0
  );

  const sampleMeta = useMemo(() => {
    const match = DEMO_DATASETS.find((dataset) => dataset.id === demoDatasetId);
    return match || DEMO_DATASETS[0];
  }, [demoDatasetId]);

  useEffect(() => {
    fetchFiles();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const mode = params.get('mode');
    const sample = params.get('sample');

    if (mode === 'demo') {
      localStorage.setItem('duckpilot_mode', 'demo');
      setDemoMode(true);

      // Show demo dataset selection modal if no files uploaded yet
      if (uploadedFiles.length === 0) {
        setShowDemoModal(true);
      }

      if (sample) {
        setDemoDatasetId(sample);
      }
    } else {
      setDemoMode(false);
      if (isAuthenticated) {
        localStorage.setItem('duckpilot_mode', 'auth');
      }
    }
  }, [location.search, isAuthenticated, uploadedFiles.length]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const checkout = params.get('checkout');
    if (!checkout) return;

    if (checkout === 'success') {
      if (params.get('session_id')) {
        confirmCheckoutSession(params.get('session_id'));
      } else {
        fetchBillingStatus();
      }
      refreshUsage();
      fetchSessionUser();
    }

    params.delete('checkout');
    const nextSearch = params.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : ''
      },
      { replace: true }
    );
  }, [location.search, location.pathname, navigate, refreshUsage, fetchSessionUser, fetchBillingStatus, confirmCheckoutSession]);

  useEffect(() => {
    try {
      const hasSeenTour = localStorage.getItem(tourStorageKey);
      if (!hasSeenTour) {
        handleStartTour();
      }
    } catch (error) {
      console.warn('Failed to read tour preference:', error);
    }
  }, []);

  useEffect(() => {
    try {
      setShowFreeBanner(localStorage.getItem('duckpilot_show_free_banner') === 'true');
    } catch (error) {
      setShowFreeBanner(false);
    }
  }, []);

  const fetchFiles = async () => {
    try {
      await duckdbService.restorePersistedDatasets();
      const files = duckdbService.getAllDatasets();
      setUploadedFiles(files);
    } catch (error) {
      console.error('Error fetching files:', error);
    }
  };

  const handleFileUpload = useCallback(async (file, options = {}) => {
    if (demoMode && !options.allowDemo) {
      setShowAuthModal(true);
      return;
    }
    if (!demoMode && !isAuthenticated) {
      setShowAuthModal(true);
      return;
    }
    setLoading(true);
    try {
      if (!demoMode && isAuthenticated) {
        await usageService.recordUpload();
        await refreshUsage();
      }
      const fileId = `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
      const response = await duckdbService.uploadFile(file, fileId);
      await fetchFiles();
      setSelectedFile(response);
      setActiveTab('query');
    } catch (error) {
      console.error('Upload error:', error);
    } finally {
      setLoading(false);
    }
  }, [demoMode, isAuthenticated, refreshUsage]);

  const handleLoadSample = useCallback(async () => {
    if (sampleLoading) return;
    setSampleLoading(true);
    try {
      const response = await fetch(sampleMeta.path);
      if (!response.ok) {
        throw new Error('Failed to load sample dataset');
      }
      const blob = await response.blob();
      const file = new File([blob], sampleMeta.filename, { type: 'text/csv' });
      await handleFileUpload(file, { allowDemo: true });
    } catch (error) {
      console.error('Sample load failed:', error);
    } finally {
      setSampleLoading(false);
    }
  }, [sampleLoading, sampleMeta, handleFileUpload]);

  // Handler to load a specific demo dataset by id
  const handleLoadDemoDataset = useCallback(async (dataset) => {
    if (sampleLoading) return;
    setDemoDatasetId(dataset.id);
    setShowDemoModal(false);
    setSampleLoading(true);
    try {
      const response = await fetch(dataset.path);
      if (!response.ok) {
        throw new Error('Failed to load demo dataset');
      }
      const blob = await response.blob();
      const file = new File([blob], dataset.filename, { type: 'text/csv' });
      await handleFileUpload(file, { allowDemo: true });
    } catch (error) {
      console.error('Demo dataset load failed:', error);
    } finally {
      setSampleLoading(false);
    }
  }, [sampleLoading, handleFileUpload]);

  const handleOnboardingQuestion = (question) => {
    setPrefillQuestion({ text: question, token: Date.now() });
    if (uploadedFiles.length === 0) {
      handleLoadSample();
      return;
    }
    setActiveTab('query');
  };

  const handleStartUpload = useCallback(() => {
    const target = document.getElementById('file-upload-dropzone');
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);

  const handleTemplateQuestion = (question) => {
    setPrefillQuestion({ text: question, token: Date.now() });
    if (!selectedFile) {
      handleLoadSample();
      return;
    }
    setActiveTab('query');
  };

  const handleStartTour = () => {
    if (window.innerWidth < 1024) {
      setSidebarOpen(true);
    }
    setShowTour(true);
  };

  const tourSteps = [
    {
      title: 'Upload data',
      description: 'Start here to add CSV, XLSX, JSON, or Parquet files.',
      selector: '[data-tour-id="upload"]'
    },
    {
      title: 'Use templates',
      description: 'Pick curated questions for common workflows.',
      selector: '[data-tour-id="templates"]'
    },
    {
      title: 'Ask questions',
      description: 'Use natural language to generate SQL and get answers.',
      selector: '[data-tour-id="query"]'
    },
    {
      title: 'Profile your data',
      description: 'See schema types, null counts, and quality metrics.',
      selector: '[data-tour-id="profile"]'
    },
    {
      title: 'Manage files',
      description: 'View, delete, and export datasets.',
      selector: '[data-tour-id="files"]'
    },
  ];

  const handleFileSelect = (file) => {
    setSelectedFile(file);
    setActiveTab('query');
  };

  const handleFileDelete = async (fileId) => {
    try {
      await duckdbService.deleteDataset(fileId);
      await fetchFiles();
      if (selectedFile?.file_id === fileId) {
        setSelectedFile(null);
      }
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  const handleClearAll = async () => {
    try {
      await duckdbService.clearAllDatasets();
      setSelectedFile(null);
      await fetchFiles();
    } catch (error) {
      console.error('Clear all failed:', error);
    }
  };

  const handleBundleImport = async (file) => {
    try {
      if (demoMode) {
        setShowAuthModal(true);
        return;
      }
      if (!isAuthenticated) {
        setShowAuthModal(true);
        return;
      }
      await usageService.recordUpload();
      await refreshUsage();
      const content = await file.text();
      const bundle = JSON.parse(content);
      const dataset = await bundleService.importBundle(bundle);
      await duckdbService.loadPersistedRecords([dataset]);
      await fetchFiles();
      setSelectedFile({
        file_id: dataset.file_id,
        filename: dataset.filename,
        columns: dataset.columns,
        row_count: dataset.row_count,
        upload_time: dataset.upload_time,
        created_at: dataset.created_at,
        file_size_bytes: dataset.file_size_bytes
      });
      setActiveTab('query');
    } catch (error) {
      console.error('Failed to import bundle:', error);
    }
  };

  const tabs = [
    { id: 'upload', label: 'Upload', icon: Upload },
    { id: 'templates', label: 'Templates', icon: LayoutGrid },
    { id: 'query', label: 'Ask Questions', icon: MessageSquare },
    { id: 'analyze', label: 'AI Analysis', icon: Brain },
    { id: 'profile', label: 'Data Profile', icon: PieChart },
    { id: 'sql', label: 'SQL Query', icon: Code },
    { id: 'view', label: 'View Data', icon: BarChart3 },
    { id: 'files', label: 'Files', icon: FileText },
  ];

  const uploadDisabledReason = demoMode
    ? 'Upload your own data by creating a free account.'
    : (!isAuthenticated ? 'Create a free account to upload and query your own files.' : null);

  const billingIssue = useMemo(() => {
    if (!billingStatus?.subscription_status) return null;
    const status = billingStatus.subscription_status;
    const issueStatuses = ['past_due', 'unpaid', 'incomplete', 'incomplete_expired', 'canceled'];
    if (!issueStatuses.includes(status)) return null;
    const messageMap = {
      past_due: 'Payment failed. Update your payment method to keep Pro features.',
      unpaid: 'Payment is overdue. Update billing to restore Pro features.',
      incomplete: 'Subscription setup is incomplete. Please finish payment.',
      incomplete_expired: 'Subscription setup expired. Restart checkout to upgrade.',
      canceled: 'Subscription canceled. Upgrade again to restore Pro features.',
    };
    return messageMap[status] || 'Billing issue detected. Please update your payment method.';
  }, [billingStatus]);

  const handleBillingPortal = async () => {
    setBillingActionLoading(true);
    try {
      const session = await billingService.createPortalSession();
      window.location.href = session.url;
    } catch (error) {
      console.error('Unable to open billing portal:', error);
    } finally {
      setBillingActionLoading(false);
    }
  };

  // Session sync is owned by AuthContext (runs once per signed-in user).
  // DashboardPage must NOT trigger syncSession itself — doing so during render
  // or in a loosely-guarded effect caused an infinite /api/auth/clerk loop.

  // Redirect to landing page if not authenticated and not in demo mode
  useEffect(() => {
    // Check URL params directly to avoid race condition with demoMode state
    const params = new URLSearchParams(location.search);
    const isDemoFromUrl = params.get('mode') === 'demo';

    if (!authLoading && !sessionLoading && !isAuthenticated && !demoMode && !isDemoFromUrl && !firebaseUser) {
      navigate('/');
    }
  }, [authLoading, sessionLoading, isAuthenticated, demoMode, firebaseUser, navigate, location.search]);

  // Check demo mode from URL to avoid race condition with state
  const isDemoFromUrl = new URLSearchParams(location.search).get('mode') === 'demo';
  const isInDemoMode = demoMode || isDemoFromUrl;

  // Loading spinner while Clerk initializes or the backend session is syncing.
  const showLoading =
    !isInDemoMode &&
    (authLoading || (firebaseUser && !isAuthenticated && sessionLoading));
  if (showLoading) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 text-sand-700">
            <svg className="h-5 w-5 animate-spin text-bill-500" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <span>Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  // Signed in, but the backend session exchange failed — surface it instead of
  // spinning forever. (sessionError is only set after an attempt completes.)
  if (!isInDemoMode && firebaseUser && !isAuthenticated && sessionError) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center px-6">
        <div className="max-w-md w-full bg-white border border-[rgba(32,30,29,0.12)] rounded-brand-lg shadow-brand-md p-8 text-center">
          <h1 className="font-display text-2xl text-ink mb-2">We couldn't reach your account</h1>
          <p className="text-sand-700 text-sm mb-6">
            You're signed in, but connecting to the DuckPilot server failed. You can retry, explore a
            demo dataset, or head back home.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="rounded-full bg-bill-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-bill-600 transition-colors shadow-brand-sm"
            >
              Retry
            </button>
            <button
              type="button"
              onClick={() => navigate('/dashboard?mode=demo')}
              className="rounded-full border border-[rgba(32,30,29,0.18)] bg-white px-5 py-2.5 text-sm font-semibold text-ink hover:bg-sand-100 transition-colors"
            >
              Try a demo
            </button>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="rounded-full px-5 py-2.5 text-sm font-semibold text-sand-700 hover:text-ink transition-colors"
            >
              Back to home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper text-ink font-body">
      <div className="flex flex-col lg:flex-row h-screen">
        {/* Mobile sidebar overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-ink/40 z-20 lg:hidden backdrop-blur-sm"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        <Sidebar
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          uploadedFiles={uploadedFiles}
          selectedFile={selectedFile}
          onFileSelect={handleFileSelect}
          sidebarOpen={sidebarOpen}
          onSidebarOpen={setSidebarOpen}
          storageUsedBytes={totalStorageBytes}
          onStartTour={handleStartTour}
          onOpenSettings={() => navigate('/settings')}
          demoMode={demoMode}
          onTryAnotherDataset={() => setShowDemoModal(true)}
        />

        <div className="flex-1 flex flex-col overflow-hidden">
          <Header
            selectedFile={selectedFile}
            onSidebarToggle={() => setSidebarOpen(!sidebarOpen)}
            onOpenSettings={() => navigate('/settings')}
            demoMode={demoMode}
            onTryAnotherDataset={() => setShowDemoModal(true)}
          />
          <main className="flex-1 overflow-auto p-4 lg:p-6 bg-paper">
            <div className="max-w-7xl mx-auto">
              {showFreeBanner && !demoMode && (
                <div className="mb-6 rounded-brand-md border border-sage-300 bg-sage-100 p-4 text-sm text-sage-900">
                  You're on the free plan. You can upload {usageStatus?.uploads_limit ?? 'X'} files and run {usageStatus?.queries_limit ?? 'Y'} queries per month.
                </div>
              )}

              {billingIssue && !demoMode && (
                <div className="mb-6 rounded-brand-md border border-bill-300 bg-bill-100 p-4 text-sm text-bill-900 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <span>{billingIssue}</span>
                  <button
                    type="button"
                    onClick={handleBillingPortal}
                    disabled={billingActionLoading}
                    className="rounded-full bg-bill-500 px-4 py-2 text-xs font-semibold text-white hover:bg-bill-600 disabled:opacity-50"
                  >
                    {billingActionLoading ? 'Opening billing...' : 'Manage billing'}
                  </button>
                </div>
              )}

              {!demoMode && usageStatus && (
                <div className="mb-6">
                  <UsageWarning
                    uploadsRemaining={usageStatus.uploads_remaining}
                    queriesRemaining={usageStatus.queries_remaining}
                    uploadsLimit={usageStatus.uploads_limit}
                    queriesLimit={usageStatus.queries_limit}
                    planType={usageStatus.plan_type}
                    variant="banner"
                    onDismiss={() => { }}
                  />
                </div>
              )}

              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="h-full"
                >
                  {activeTab === 'upload' && (
                    <div className="space-y-6">
                      <div className="text-center mb-8">
                        <h1 className="font-display text-3xl lg:text-4xl text-ink mb-3">
                          Drop a CSV. Ask it anything.
                        </h1>
                        <p className="text-sand-700 max-w-xl mx-auto">
                          Your file opens right here in the browser. Plain-English questions in,
                          answers with tables and charts out — nothing ever uploads.
                        </p>
                      </div>
                      {uploadedFiles.length === 0 && (
                        <Onboarding
                          onLoadSample={demoMode ? () => setShowDemoModal(true) : handleLoadSample}
                          onStartUpload={handleStartUpload}
                          onQuestionSelect={handleOnboardingQuestion}
                          sampleLoading={sampleLoading}
                          sampleMeta={sampleMeta}
                          isAuthenticated={isAuthenticated}
                          onRequireAuth={() => setShowAuthModal(true)}
                          demoMode={demoMode}
                        />
                      )}
                      <FileUpload
                        onFileUpload={handleFileUpload}
                        loading={loading || sampleLoading}
                        disabled={Boolean(uploadDisabledReason)}
                        disabledReason={uploadDisabledReason}
                        onDisabledAction={() => setShowAuthModal(true)}
                        disabledActionLabel="Create free account"
                      />
                    </div>
                  )}

                  {activeTab === 'templates' && (
                    <Templates
                      selectedFile={selectedFile}
                      onUseQuestion={handleTemplateQuestion}
                      onLoadSample={handleLoadSample}
                    />
                  )}

                  {activeTab === 'query' && (
                    <QueryInterface
                      selectedFile={selectedFile}
                      onFileSelect={setSelectedFile}
                      prefillQuestion={prefillQuestion.text}
                      prefillToken={prefillQuestion.token}
                      mode={demoMode ? 'demo' : 'auth'}
                      isAuthenticated={isAuthenticated}
                      onRequireAuth={() => setShowAuthModal(true)}
                      onUsageUpdate={refreshUsage}
                    />
                  )}

                  {activeTab === 'analyze' && (
                    <BusinessAnalysis
                      selectedFile={selectedFile}
                      onFileSelect={setSelectedFile}
                      mode={demoMode ? 'demo' : 'auth'}
                      isAuthenticated={isAuthenticated}
                      onRequireAuth={() => setShowAuthModal(true)}
                      onUsageUpdate={refreshUsage}
                    />
                  )}

                  {activeTab === 'profile' && (
                    <DataProfile
                      selectedFile={selectedFile}
                      onQuestionClick={(question) => {
                        setPrefillQuestion({ text: question, token: Date.now() });
                        setActiveTab('query');
                      }}
                    />
                  )}

                  {activeTab === 'sql' && (
                    <SQLQueryInterface
                      selectedFile={selectedFile}
                      onFileSelect={setSelectedFile}
                      mode={demoMode ? 'demo' : 'auth'}
                      isAuthenticated={isAuthenticated}
                      onRequireAuth={() => setShowAuthModal(true)}
                      onUsageUpdate={refreshUsage}
                    />
                  )}

                  {activeTab === 'view' && (
                    <DataViewer
                      selectedFile={selectedFile}
                      onFileSelect={setSelectedFile}
                    />
                  )}

                  {activeTab === 'files' && (
                    <FileManager
                      files={uploadedFiles}
                      onFileSelect={handleFileSelect}
                      onFileDelete={handleFileDelete}
                      onRefresh={fetchFiles}
                      onClearAll={handleClearAll}
                      onBundleImport={handleBundleImport}
                      uploadDisabled={Boolean(uploadDisabledReason)}
                      uploadDisabledReason={uploadDisabledReason}
                      onRequireAuth={() => setShowAuthModal(true)}
                    />
                  )}

                </motion.div>
              </AnimatePresence>
            </div>
          </main>
        </div>
      </div>

      <TourOverlay
        isOpen={showTour}
        onClose={() => {
          setShowTour(false);
          try {
            localStorage.setItem(tourStorageKey, 'true');
          } catch (error) {
            console.warn('Failed to store tour preference:', error);
          }
        }}
        steps={tourSteps}
      />

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onAuthSuccess={() => navigate('/dashboard')}
      />
      {/* <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onRequireAuth={() => setShowAuthModal(true)}
      /> */}
      <DemoDatasetModal
        isOpen={showDemoModal}
        onClose={() => setShowDemoModal(false)}
        datasets={DEMO_DATASETS}
        onSelect={handleLoadDemoDataset}
      />
    </div>
  );
}

export default DashboardPage;
